import {Box, type RelativeTime} from '@primer/react'
import {Fragment, type ReactElement} from 'react'

import type {Author} from './commit-attribution-types'
import {AuthorsDialog} from './components/AuthorsDialog'
import {CommitAuthorStack} from './components/CommitAuthorStack'
import {AuthorLink} from './components/AuthorLink'
import type {Repository} from '@github-ui/current-repository'
import {AuthorAvatar} from './components/AuthorAvatar'

export interface CommitAttributionProps {
  authors: Author[]
  committer: Author
  committerAttribution: boolean
  includeVerbs: boolean
  includeAuthorTooltip?: boolean
  repo: Repository
  avatarSize?: 16 | 20
  relativeTime?: ReactElement<typeof RelativeTime>
}

function SingleAuthor({
  author,
  repo,
  avatarSize,
  includeTooltip,
}: {
  author: Author
  repo: Repository
  avatarSize: CommitAttributionProps['avatarSize']
  includeTooltip?: boolean
}) {
  return <AuthorAvatar author={author} repo={repo} avatarSize={avatarSize} includeTooltip={includeTooltip} />
}

function AuthorAndCommitter({
  author,
  committer,
  repo,
  avatarSize,
  includeTooltip,
}: {
  author: Author
  committer: Author
  repo: Repository
  avatarSize: CommitAttributionProps['avatarSize']
  includeTooltip?: boolean
}) {
  return (
    <>
      <CommitAuthorStack authors={[author, committer]} avatarSize={avatarSize} />
      <AuthorLink author={author} repo={repo} sx={{pl: 1}} includeTooltip={includeTooltip} />
    </>
  )
}

function TwoAuthors({
  authors,
  repo,
  avatarSize,
  includeTooltip,
}: {
  authors: Author[]
  repo: Repository
  avatarSize: CommitAttributionProps['avatarSize']
  includeTooltip?: boolean
}) {
  return (
    <>
      <CommitAuthorStack authors={authors} avatarSize={avatarSize} />
      {authors.map((author, index) => (
        <Fragment key={`${author.login}_${index}`}>
          <AuthorLink author={author} repo={repo} sx={{pl: 1}} includeTooltip={includeTooltip} />
          {index !== authors.length - 1 && (
            <Box as="span" sx={{pl: 1}}>
              and
            </Box>
          )}
        </Fragment>
      ))}
    </>
  )
}

function MultipleAuthors({
  authors,
  repo,
  avatarSize,
}: {
  authors: Author[]
  repo: Repository
  avatarSize: CommitAttributionProps['avatarSize']
}) {
  return (
    <>
      <CommitAuthorStack authors={authors} avatarSize={avatarSize} />
      <AuthorsDialog authors={authors} repo={repo} />
    </>
  )
}

export function CommitAttribution({
  authors,
  committer,
  committerAttribution,
  repo,
  avatarSize,
  relativeTime,
  includeVerbs = true,
  includeAuthorTooltip = true,
}: CommitAttributionProps) {
  const singleAuthor = authors.length === 1 && !committerAttribution
  const authorAndCommitter = authors.length === 1 && committerAttribution
  const inlineAuthorNames = authors.length === 2 && !committerAttribution
  const multipleAuthors = !singleAuthor && !authorAndCommitter && !inlineAuthorNames

  const verbSx = includeVerbs ? {pl: 1} : {}

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center'}}>
      {singleAuthor && (
        <SingleAuthor author={authors[0]!} repo={repo} avatarSize={avatarSize} includeTooltip={includeAuthorTooltip} />
      )}
      {authorAndCommitter && (
        <AuthorAndCommitter
          author={authors[0]!}
          committer={committer}
          repo={repo}
          avatarSize={avatarSize}
          includeTooltip={includeAuthorTooltip}
        />
      )}
      {inlineAuthorNames && (
        <TwoAuthors authors={authors} repo={repo} avatarSize={avatarSize} includeTooltip={includeAuthorTooltip} />
      )}
      {multipleAuthors && <MultipleAuthors authors={authors} repo={repo} avatarSize={avatarSize} />}

      {!committerAttribution ? (
        <Box as="span" sx={verbSx}>
          {includeVerbs && 'committed'}
        </Box>
      ) : (
        <>
          <Box as="span" sx={verbSx}>
            {includeVerbs ? 'authored and' : 'and'}
          </Box>
          <AuthorLink author={committer} repo={repo} sx={{pl: 1}} includeTooltip={includeAuthorTooltip} />
          <Box as="span" sx={verbSx}>
            {includeVerbs && 'committed'}
          </Box>
        </>
      )}

      {relativeTime}
    </Box>
  )
}

try{ SingleAuthor.displayName ||= 'SingleAuthor' } catch {}
try{ AuthorAndCommitter.displayName ||= 'AuthorAndCommitter' } catch {}
try{ TwoAuthors.displayName ||= 'TwoAuthors' } catch {}
try{ MultipleAuthors.displayName ||= 'MultipleAuthors' } catch {}
try{ CommitAttribution.displayName ||= 'CommitAttribution' } catch {}